import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Building2, CheckCircle2, DollarSign, Users, TrendingUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";

// Use environment variable with fallback to current origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function CompanySignup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    password: "",
    address: "",
    cities: "",
    management_type: "",  // DEPRECATED - kept for backward compatibility
    management_types: ["Residential", "Condo", "Commercial"],  // Default to all types
    service_radius_km: 50,  // Default 50km radius
    description: "",
    website: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleManagementTypeToggle = (type) => {
    setFormData(prev => {
      const types = [...prev.management_types];
      const index = types.indexOf(type);
      
      if (index > -1) {
        // Remove type if already selected (but keep at least one)
        if (types.length > 1) {
          types.splice(index, 1);
        } else {
          toast.error("You must select at least one management type");
          return prev;
        }
      } else {
        // Add type if not selected
        types.push(type);
      }
      
      return { ...prev, management_types: types };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Parse cities
      const cities = formData.cities.split(",").map(c => c.trim()).filter(c => c);

      if (cities.length === 0) {
        toast.error("Please enter at least one city");
        setSubmitting(false);
        return;
      }

      // Generate postal code prefixes based on cities (for backward compatibility)
      // This will be empty and matching will be done by city + radius
      const postal_code_prefixes = [];

      await axios.post(`${API}/companies`, {
        ...formData,
        cities,
        postal_code_prefixes
      });

      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error("Error submitting application:", error);
      
      // Check if it's a duplicate account error
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail, {
          duration: 8000,
          action: {
            label: "Go to Login",
            onClick: () => navigate("/property-manager-login")
          }
        });
      } else {
        toast.error("Failed to submit application. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
        <Header variant="full" />

        <div className="container mx-auto px-6 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center h-20 w-20 rounded-full mb-6" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
              <CheckCircle2 className="h-10 w-10" style={{ color: '#3b82f6' }} />
            </div>
            <h1 className="text-4xl font-bold mb-4" style={{ color: '#1E1E1E' }}>
              Application Received!
            </h1>
            <p className="text-lg mb-8" style={{ color: '#3A3A3A' }}>
              Thank you for your interest in joining Top Prop's network of property management companies.
            </p>
            <Card className="bg-white text-left" style={{ border: '1px solid #F7F7F7', borderRadius: '12px' }}>
              <CardContent className="p-8 space-y-4">
                <h3 className="text-xl font-semibold" style={{ color: '#1E1E1E' }}>What happens next?</h3>
                <ul className="space-y-3" style={{ color: '#3A3A3A' }}>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
                    <span>Our team will review your application within 1-2 business days</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
                    <span>Once approved, you'll receive payment instructions for the $199/month subscription</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
                    <span>After payment is confirmed, you'll start receiving qualified leads immediately</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#3b82f6' }} />
                    <span>Login to your dashboard using your email and password to view and manage your leads</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Button
              onClick={() => navigate("/")}
              className="mt-8 text-white font-semibold transition-all duration-300"
              size="lg"
              style={{ 
                backgroundColor: '#3b82f6',
                borderRadius: '10px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                e.currentTarget.style.color = '#000000';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.color = '#FFFFFF';
              }}
            >
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      <Header variant="full" />
      {/* Hero Section */}
      <section className="container mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#1E1E1E' }}>
            Join Top Prop's Network
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#3A3A3A' }}>
            Connect with property owners across Canada who are actively seeking professional management services
          </p>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-12 max-w-5xl mx-auto">
          <Card className="bg-white" style={{ border: '1px solid #F7F7F7', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
            <CardContent className="p-6 text-center">
              <div className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                <Users className="h-6 w-6" style={{ color: '#3b82f6' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#1E1E1E' }}>Qualified Leads</h3>
              <p className="text-sm" style={{ color: '#3A3A3A' }}>
                Receive leads from property owners actively looking for management services in your area
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white" style={{ border: '1px solid #F7F7F7', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
            <CardContent className="p-6 text-center">
              <div className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(244, 162, 97, 0.15)' }}>
                <TrendingUp className="h-6 w-6" style={{ color: '#F4A261' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#1E1E1E' }}>Grow Your Business</h3>
              <p className="text-sm" style={{ color: '#3A3A3A' }}>
                Expand your portfolio with new properties across your service areas
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white" style={{ border: '1px solid #F7F7F7', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
            <CardContent className="p-6 text-center">
              <div className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                <DollarSign className="h-6 w-6" style={{ color: '#3b82f6' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#1E1E1E' }}>$199/Month</h3>
              <p className="text-sm" style={{ color: '#3A3A3A' }}>
                Simple pricing with a 1-year contract. Cancel anytime with 30 days notice
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Signup Form */}
        <div className="max-w-3xl mx-auto">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-2xl">Company Application</CardTitle>
              <CardDescription>
                Fill out the form below to join our network. We'll review your application and get back to you within 1-2 business days.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="company-signup-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      data-testid="company-name-input"
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="ABC Property Management"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_person">Contact Person *</Label>
                    <Input
                      id="contact_person"
                      data-testid="contact-person-input"
                      required
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (Cell Phone Required) *</Label>
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                    <p className="text-xs text-blue-600 font-medium">📱 Must be a cell phone that can receive text messages for lead alerts</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create a secure password"
                    minLength={8}
                  />
                  <p className="text-xs text-slate-500">Minimum 8 characters. You'll use this to access your lead dashboard.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Company Address *</Label>
                  <Input
                    id="address"
                    data-testid="address-input"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, Toronto, ON M5V 2T6"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cities">Cities You Service *</Label>
                  <Input
                    id="cities"
                    data-testid="cities-input"
                    required
                    value={formData.cities}
                    onChange={(e) => setFormData({ ...formData, cities: e.target.value })}
                    placeholder="Toronto, Mississauga, Brampton (comma-separated)"
                  />
                  <p className="text-xs text-slate-500">Enter cities separated by commas</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service_radius">Service Radius (km) *</Label>
                  <Input
                    id="service_radius"
                    type="number"
                    min="10"
                    max="200"
                    required
                    value={formData.service_radius_km}
                    onChange={(e) => setFormData({ ...formData, service_radius_km: parseInt(e.target.value) || 50 })}
                    placeholder="50"
                  />
                  <p className="text-xs text-slate-500">How many kilometers from your cities will you service? (e.g., Ottawa + 50km covers Carp, Kanata, etc.)</p>
                </div>

                <div className="space-y-2">
                  <Label>Management Types * (Select all that apply)</Label>
                  <div className="space-y-3 p-4 border border-slate-200 rounded-lg bg-slate-50">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="type-residential"
                        checked={formData.management_types.includes("Residential")}
                        onChange={() => handleManagementTypeToggle("Residential")}
                        className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="type-residential" className="text-sm font-medium text-slate-900 cursor-pointer">
                        Residential Property Management
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="type-condo"
                        checked={formData.management_types.includes("Condo")}
                        onChange={() => handleManagementTypeToggle("Condo")}
                        className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="type-condo" className="text-sm font-medium text-slate-900 cursor-pointer">
                        Condo / Strata Management
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="type-commercial"
                        checked={formData.management_types.includes("Commercial")}
                        onChange={() => handleManagementTypeToggle("Commercial")}
                        className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="type-commercial" className="text-sm font-medium text-slate-900 cursor-pointer">
                        Commercial Property Management
                      </label>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">Select the types of properties your company manages (at least one required)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Company Website *</Label>
                  <Input
                    id="website"
                    data-testid="website-input"
                    type="text"
                    required
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="www.yourcompany.com or https://www.yourcompany.com"
                  />
                  <p className="text-xs text-slate-500">Enter your company website (e.g., www.yourcompany.com)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Company Description</Label>
                  <Textarea
                    id="description"
                    data-testid="description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Tell property owners about your services..."
                    rows={4}
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Subscription Details</h4>
                  <ul className="text-sm text-slate-700 space-y-1">
                    <li>• Monthly Fee: $199/month</li>
                    <li>• Contract Term: 1 year</li>
                    <li>• Payment required after approval</li>
                    <li>• Access to all leads in your service areas</li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  className="w-full text-white py-6 text-lg font-semibold transition-all duration-300"
                  style={{ 
                    backgroundColor: '#3b82f6',
                    borderRadius: '12px',
                    boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                      e.currentTarget.style.color = '#000000';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  disabled={submitting}
                  data-testid="submit-button"
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-12 mt-16" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6" style={{ color: '#3b82f6' }} />
              <span className="text-xl font-bold">Top Prop</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <button
                onClick={() => navigate("/contact")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Contact Us
              </button>
              <span style={{ color: '#3A3A3A' }}>|</span>
              <button
                onClick={() => navigate("/company-signup")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Join as Property Manager
              </button>
            </div>
            <p className="text-sm" style={{ color: '#9C9C9C' }}>© 2025 Top Prop. All rights reserved.</p>
            
            {/* Admin Login Link */}
            <div className="mt-4">
              <button
                onClick={() => navigate("/admin-login")}
                className="text-xs underline transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#F4A261'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Admin Login
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}